// questEngine.js
// Drives optional SIDE QUESTS (parallel to the main campaign milestone chain). Each side
// quest is a milestone-shaped chain, so it reuses the milestone engine for step
// completion. The game loop checks an event against the main campaign AND every active
// side quest. See docs/QUESTS_AND_SITES_PLAN.md.

import { SIDE_QUESTS } from '../data/sideQuests';

// --- step matching helpers ---------------------------------------------------
// A step's prerequisites (other step ids in the same quest) are all completed.
const requiresMet = (step, milestones) =>
  (step.requires || []).every((rid) => milestones.find((m) => m.id === rid)?.completed);

const stepRewards = (step) => step.rewards || { xp: 0, gold: 0, items: [] };

// Apply a game event to a step's trigger. Supports a `count` (collect/defeat N) — each
// matching event bumps progress, completing only at the threshold. Returns the new
// progress + whether the step is now complete, or null if the event doesn't match.
const applyEventToStep = (step, event) => {
  const t = step.trigger || {};
  let matched = false;
  if (event.type === 'item_acquired' && t.item && event.itemId === t.item) matched = true;
  else if (event.type === 'enemy_defeated' && t.enemy && (t.enemy === 'any' || event.enemyId === t.enemy)) matched = true;
  else if (event.type === 'location_visited' && t.location && event.locationId === t.location) matched = true;
  if (!matched) return null;
  const need = t.count || 1;
  const progress = (step.progress || 0) + 1;
  return { progress, completed: progress >= need };
};

// Does a turn-in step accept being handed in at the given context (building/town)?
const turnInMatches = (turnIn, ctx = {}) => {
  if (!turnIn) return false;
  if (turnIn.building) {
    const buildings = Array.isArray(turnIn.building) ? turnIn.building : [turnIn.building];
    if (!buildings.includes(ctx.buildingType)) return false;
  }
  if (turnIn.location && ctx.townName !== turnIn.location) return false;
  return true;
};

// --- Quest origin town (playtest 2026-07-18) --------------------------------
// A side quest's giver lives in a real town. Even a wilderness RUMOUR is "word from the
// guild of somewhere": we anchor the quest to a real town so the offer can name it and
// the turn-in points back to it (restricted via turnIn.location). We never invent a guild
// name — we use the town's ACTUAL generated building name when the town is already mapped,
// otherwise just the town + building type (both real).

// First building name of `type` (or any of an array) in a cached town sub-map, or null.
const buildingNameInTown = (townMap, type) => {
  const grid = townMap?.mapData;
  if (!Array.isArray(grid) || !type) return null;
  const types = Array.isArray(type) ? type : [type];
  for (const row of grid) {
    if (!Array.isArray(row)) continue;
    for (const tile of row) {
      if (tile && types.includes(tile.buildingType) && tile.buildingName) return tile.buildingName;
    }
  }
  return null;
};

/**
 * Choose a real origin town for a side quest's giver building. Prefers the town the party
 * is currently in; otherwise the nearest named town on the world map, preferring one
 * already visited so its ACTUAL building name is available. Returns
 * `{ town, buildingName }` (buildingName null when the town isn't mapped yet) or null when
 * the world map has no towns.
 * @param {Object} quest
 * @param {Object} ctx { worldMap, townMapsCache, currentTownName, playerPos:{x,y} }
 */
export const resolveQuestOrigin = (quest, { worldMap, townMapsCache = {}, currentTownName = null, playerPos = null } = {}) => {
  const giver = quest?.giver?.building;
  if (!giver) return null;
  // A quest given a home town at world creation always comes from there.
  if (quest.giver.town) {
    return { town: quest.giver.town, buildingName: quest.giver.buildingName || buildingNameInTown(townMapsCache[quest.giver.town], giver) };
  }
  if (currentTownName) {
    return { town: currentTownName, buildingName: buildingNameInTown(townMapsCache[currentTownName], giver) };
  }
  const towns = [];
  (worldMap || []).forEach((row) => (row || []).forEach((t) => {
    if (t && t.poi === 'town' && t.townName) towns.push(t);
  }));
  if (towns.length === 0) return null;
  const dist = (t) => (playerPos ? Math.abs(t.x - playerPos.x) + Math.abs(t.y - playerPos.y) : 0);
  towns.sort((a, b) => dist(a) - dist(b));
  // Nearest town that already carries a real giver-building name wins; else the nearest.
  const named = towns.find((t) => buildingNameInTown(townMapsCache[t.townName], giver));
  const chosen = named || towns[0];
  return { town: chosen.townName, buildingName: buildingNameInTown(townMapsCache[chosen.townName], giver) };
};

/**
 * Immutably stamp a resolved origin onto a quest: giver.town + giver.buildingName, and
 * turnIn.location on every turn-in step so the hand-in is restricted to that town.
 */
export const stampQuestOrigin = (quest, origin) => {
  if (!quest || !origin?.town) return quest;
  return {
    ...quest,
    giver: { ...quest.giver, town: origin.town, buildingName: origin.buildingName || null },
    milestones: (quest.milestones || []).map((m) =>
      m?.trigger?.turnIn
        ? { ...m, trigger: { ...m.trigger, turnIn: { ...m.trigger.turnIn, location: origin.town } } }
        : m
    ),
  };
};

// Side quests per settlement, by size: a few per town, more in bigger places (maintainer
// 2026-10-06: "a few per town, just not ALL side quests in one town and not all at the
// same time"). Summed per world and capped; TOWN_OFFER_CAP paces how many are in play.
export const TOWN_QUEST_QUOTA = { city: 3, town: 2, village: 1, hamlet: 1 };
export const MAX_WORLD_SIDE_QUESTS = 8;
export const TOWN_OFFER_CAP = 2;

/**
 * Side-quest plan for a world from its settlements: how many quests in total, each town's
 * quota, and the towns that should get a level-1 quest (the starting town, then cities).
 * @param {Array<{ townName: string, townSize?: string, isStartingTown?: boolean }>} towns
 */
export const planSideQuests = (towns) => {
  const list = (towns || []).filter((t) => t?.townName);
  const quotas = {};
  list.forEach((t) => { quotas[t.townName] = TOWN_QUEST_QUOTA[t.townSize] || 1; });
  const total = Math.min(MAX_WORLD_SIDE_QUESTS, Math.max(2, Object.values(quotas).reduce((a, b) => a + b, 0)));
  const start = list.filter((t) => t.isStartingTown).map((t) => t.townName);
  const cities = list.filter((t) => t.townSize === 'city' && !t.isStartingTown).map((t) => t.townName).sort();
  const levelOneTowns = [...start, ...cities];
  // Fill order for selectTownSideQuests: level-one towns first, then the rest by size.
  const rank = { city: 0, town: 1, village: 2, hamlet: 3 };
  const others = list.filter((t) => !levelOneTowns.includes(t.townName))
    .sort((a, b) => ((rank[a.townSize] ?? 4) - (rank[b.townSize] ?? 4)) || a.townName.localeCompare(b.townName))
    .map((t) => t.townName);
  // Trim quotas to the world cap, taking from the smallest settlements last in order.
  let budget = total;
  const order = [...levelOneTowns, ...others].map((town) => {
    const quota = Math.min(quotas[town], budget);
    budget -= quota;
    return { town, quota, levelOne: levelOneTowns.includes(town) };
  }).filter((t) => t.quota > 0);
  return { total, quotas, levelOneTowns, order };
};

/**
 * Give each quest without a town a home town: one whose cached map has the giver
 * building. Spreads quests out by preferring the town and building with the fewest quests
 * already (counting `existing` quests that are not completed), with ties broken by `rng`,
 * so each world places its quests differently. Quests whose giver building isn't in any
 * cached town are returned unchanged (any town with the building may offer them).
 * @param {Array} quests - quests to place (returned as new objects; input untouched).
 * @param {Object} townMapsCache - townName -> town map.
 * @param {() => number} rng
 * @param {Array} [existing] - quests already in the save.
 * @returns {Array}
 */
export const assignHomeTowns = (quests, townMapsCache, rng = Math.random, existing = []) => {
  const towns = Object.keys(townMapsCache || {}).sort();
  const typesIn = {};
  towns.forEach((name) => {
    const set = new Set();
    (townMapsCache[name]?.mapData || []).forEach((row) => (row || []).forEach((t) => {
      if (t?.type === 'building' && t.buildingType) set.add(t.buildingType);
    }));
    typesIn[name] = set;
  });
  const perTown = {};
  const perSlot = {};
  const count = (town, type) => {
    perTown[town] = (perTown[town] || 0) + 1;
    perSlot[`${town}|${type}`] = (perSlot[`${town}|${type}`] || 0) + 1;
  };
  const giverTypes = (q) => (Array.isArray(q?.giver?.building) ? q.giver.building : [q?.giver?.building]).filter(Boolean);
  (existing || []).forEach((q) => {
    if (q?.status === 'completed' || !q?.giver?.town) return;
    const type = giverTypes(q).find((b) => typesIn[q.giver.town]?.has(b));
    if (type) count(q.giver.town, type);
  });
  return (quests || []).map((q) => {
    if (q?.giver?.town) return q;
    const options = [];
    towns.forEach((town) => giverTypes(q).forEach((type) => {
      if (typesIn[town].has(type)) options.push({ town, type, roll: rng() });
    }));
    if (options.length === 0) return q;
    options.sort((a, b) => ((perSlot[`${a.town}|${a.type}`] || 0) - (perSlot[`${b.town}|${b.type}`] || 0))
      || ((perTown[a.town] || 0) - (perTown[b.town] || 0)) || (a.roll - b.roll));
    const pick = options[0];
    count(pick.town, pick.type);
    return stampQuestOrigin(q, { town: pick.town, buildingName: buildingNameInTown(townMapsCache[pick.town], pick.type) });
  });
};

/**
 * Can this quest be both STARTED and COMPLETED on the generated map? It must have a giver
 * building that exists, every site objective's site type must exist, and every turn-in
 * building must exist — otherwise the quest would be unstartable or uncompletable.
 * @param {Object} quest
 * @param {{ sites?: {cave?:boolean, ruins?:boolean}, buildings?: string[], theme?: string,
 *   darkness?: string }} availability
 */
export const isQuestEligible = (quest, availability = {}) => {
  const sites = availability.sites || {};
  const buildingSet = new Set(availability.buildings || []);
  const hasBuilding = (b) => (Array.isArray(b) ? b : [b]).some((x) => buildingSet.has(x));

  // biome-themed quests (`themes`) only fit worlds of that biome (settings.theme)
  if (Array.isArray(quest.themes) && !quest.themes.includes(availability.theme || 'grassland')) return false;
  // tone: 'horror' quests need a Dark game, 'light' ones stay out of Dark games
  const dark = availability.darkness === 'Dark';
  if (quest.tone === 'horror' && !dark) return false;
  if (quest.tone === 'light' && dark) return false;
  // startable: the giver building must exist on the map
  if (!quest.giver?.building || !hasBuilding(quest.giver.building)) return false;
  // completable: every step's site / turn-in target must exist
  for (const m of quest.milestones) {
    if (m.site && !sites[m.site.type]) return false;
    // Gather steps carry a `sites` source hint (where the item can actually be
    // harvested); the quest is completable if ANY of those site types exists.
    if (Array.isArray(m.sites) && m.sites.length > 0 && !m.sites.some((t) => sites[t])) return false;
    if (m.trigger?.turnIn?.building && !hasBuilding(m.trigger.turnIn.building)) return false;
  }
  return true;
};

/**
 * Pick a few side quests from the pool that can be fully started AND completed on the
 * generated map (see isQuestEligible). Returns fresh, mutable 'available' copies.
 * @param {{ sites?: object, buildings?: string[] }} availability - map's sites + building types.
 * @param {number} count - how many to include (default 2).
 * @param {() => number} rng - 0..1 RNG for a reproducible pick (default Math.random).
 */
export const selectSideQuests = (availability = {}, count = 2, rng = Math.random, pool = SIDE_QUESTS) => {
  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
  };
  const eligible = pool.filter((q) => isQuestEligible(q, availability));
  // Guarantee at least one low-tier (minLevel<=2) quest so early game has something to do,
  // then fill the rest at random. Tiering then reveals higher quests as the party levels.
  const low = shuffle(eligible.filter((q) => (q.minLevel || 1) <= 2));
  const rest = shuffle(eligible.filter((q) => (q.minLevel || 1) > 2));
  const ordered = low.length ? [low[0], ...shuffle([...low.slice(1), ...rest])] : shuffle(rest);
  return ordered.slice(0, count).map((q) => ({
    ...q,
    milestones: q.milestones.map((m) => ({ ...m, completed: false, progress: 0 })),
    status: 'available',
  }));
};

/**
 * New-game side quests, filled TOWN BY TOWN so each settlement gets its quota from quests
 * its own buildings can give (no town ends up holding quests that could only go there).
 * Order: the starting town, then cities, then the rest; each level-one town takes a
 * level-1 quest first (level 2 if none fits). Quests are placed in their town directly.
 * @param {object} availability - as selectSideQuests (sites, buildings, theme, darkness)
 * @param {Array<{ town: string, quota: number, buildings: string[], levelOne?: boolean }>} towns
 * @param {() => number} rng
 * @param {Object} townMapsCache - for the giver building's name
 */
export const selectTownSideQuests = (availability = {}, towns = [], rng = Math.random, townMapsCache = {}, pool = SIDE_QUESTS) => {
  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
  };
  const eligible = shuffle(pool.filter((q) => isQuestEligible(q, availability)));
  const taken = new Set();
  const out = [];
  const giverTypes = (q) => (Array.isArray(q.giver.building) ? q.giver.building : [q.giver.building]);
  towns.forEach(({ town, quota, buildings, levelOne }) => {
    const have = new Set(buildings || []);
    const fits = (q) => !taken.has(q.id) && giverTypes(q).some((b) => have.has(b));
    const picks = [];
    if (levelOne) {
      const first = eligible.find((q) => fits(q) && (q.minLevel || 1) <= 1) || eligible.find((q) => fits(q) && (q.minLevel || 1) === 2);
      if (first) { picks.push(first); taken.add(first.id); }
    }
    eligible.forEach((q) => { if (picks.length < quota && fits(q)) { picks.push(q); taken.add(q.id); } });
    picks.forEach((q) => {
      const type = giverTypes(q).find((b) => have.has(b));
      out.push(stampQuestOrigin({
        ...q,
        milestones: q.milestones.map((m) => ({ ...m, completed: false, progress: 0 })),
        status: 'available',
      }, { town, buildingName: buildingNameInTown(townMapsCache[town], type) }));
    });
  });
  return out;
};

// --- side-quest backfill (#45) ------------------------------------------------
// A save keeps only the quests selected at world-gen; when the SIDE_QUESTS pool grows,
// in-progress saves top up ON LOAD from the enlarged pool. Additive only: existing
// quests (any status) are never touched, only brand-new 'available' copies append.

/**
 * Availability snapshot for side-quest selection AT LOAD, derived exactly the way the
 * new-game launcher derives it (campaignLauncher) so backfill eligibility matches the
 * initial selection: sites from the persisted world map, building types unioned from
 * the cached town maps. Saves made since town pre-generation carry EVERY town in the
 * cache, so this is the exact placed-building set; older lazy-gen saves list only
 * visited towns, which under-offers (never over-offers): every backfilled quest's
 * giver is confirmed placed. townCount feeds the backfill cap.
 * @returns {{ sites: {cave:boolean, ruins:boolean}, buildings: string[], townCount: number }}
 */
export const deriveSideQuestAvailability = (worldMap, townMapsCache) => {
  const flatTiles = [].concat(...(Array.isArray(worldMap) ? worldMap : []));
  const sites = {
    cave: flatTiles.some((t) => t?.poi === 'cave_entrance'),
    ruins: flatTiles.some((t) => t?.poi === 'ruins'),
    // Open (never-hidden) site types: gather quests hint these as item sources, so
    // eligibility must know whether they exist on the map.
    forest: flatTiles.some((t) => t?.poi === 'forest'),
    hills: flatTiles.some((t) => t?.poi === 'hills'),
    mountain: flatTiles.some((t) => t?.poi === 'mountain'),
  };
  const buildings = new Set();
  Object.values(townMapsCache || {}).forEach((tm) => {
    (tm?.mapData || []).forEach((row) => (row || []).forEach((t) => {
      if (t?.type === 'building' && t.buildingType) buildings.add(t.buildingType);
    }));
  });
  const townCount = flatTiles.filter((t) => t?.poi === 'town').length;
  return { sites, buildings: [...buildings], townCount };
};

/**
 * Which pool quests can be ADDED to a save (pure; deterministic given rng). Eligibility
 * is identical to the initial selection (isQuestEligible via selectSideQuests: giver
 * building exists, every site type + turn-in building exists), excluding every quest id
 * already in the save REGARDLESS of status (completed included, so no repeats).
 *
 * Cap: new-game selection picks base = min(4, max(2, townCount)) quests; backfill tops
 * the save's OUTSTANDING (non-completed) quests up to base + 2. Rationale: the cap
 * limits the player's open queue, not lifetime history (counting completed quests
 * would starve exactly the long-running saves this feature targets), and the +2
 * headroom over the new-game count is what lets a save that already holds its full
 * initial selection actually see new pool content.
 *
 * Level: only quests within reach soon (minLevel <= level + 2, level = effective party
 * level) are added, mirroring the chain-continuation tier gate, so a low-level party's
 * few cap slots aren't consumed by quests it couldn't see for many sessions.
 *
 * @param {Array} existingSideQuests - settings.sideQuests (absent/[] for very old saves)
 * @param {{ availability?: {sites?:object, buildings?:string[], townCount?:number},
 *           level?: number, rng?: () => number, pool?: Array }} opts
 * @returns {Array} fresh 'available' quest copies to APPEND (possibly empty)
 */
export const backfillSideQuests = (existingSideQuests, { availability = {}, level = 1, rng = Math.random, pool = SIDE_QUESTS } = {}) => {
  const existing = Array.isArray(existingSideQuests) ? existingSideQuests : [];
  const baseCount = Math.min(4, Math.max(2, availability.townCount ?? 4));
  const cap = baseCount + 2;
  const outstanding = existing.filter((q) => q?.status !== 'completed').length;
  const room = cap - outstanding;
  if (room <= 0) return [];
  const existingIds = new Set(existing.map((q) => q?.id));
  const candidates = pool.filter((q) => !existingIds.has(q.id) && (q.minLevel || 1) <= level + 2);
  if (candidates.length === 0) return [];
  return selectSideQuests({ ...availability, sites: availability.sites || {}, buildings: availability.buildings || [] }, room, rng, candidates);
};

/**
 * Load-path wrapper: apply the side-quest backfill to a loaded save's settings.
 * Pure; returns the SAME settings reference when there is nothing to do (cheap skip).
 *
 * Version guard: settings.sideQuestPoolSize records the pool size the save last saw;
 * when it equals the current pool size the whole computation is skipped, so eligibility
 * never reruns on every load, only when the pool actually changed (or the save has
 * never been stamped: pre-feature saves, including ones with NO sideQuests field at
 * all, which get a fresh selection-equivalent backfill).
 *
 * Determinism: the rng is an LCG seeded off worldSeed + poolSize (same LCG family as
 * the launcher/chain), so re-running before the stamp persists (e.g. a crash between
 * compute and autosave) adds the exact same quests, never duplicates or rerolls.
 *
 * A missing/empty world map skips WITHOUT stamping, so a later load with a good map
 * can still backfill.
 *
 * @param {object} settings - parsed game_settings from the save row
 * @param {{ worldMap?: Array, townMapsCache?: object, party?: Array }} ctx
 * @param {Array} [pool]
 * @returns {{ settings: object, added: Array }}
 */
export const applySideQuestBackfill = (settings, { worldMap, townMapsCache, party } = {}, pool = SIDE_QUESTS) => {
  const poolSize = pool.length;
  if (!settings || settings.sideQuestPoolSize === poolSize) return { settings, added: [] };
  if (!Array.isArray(worldMap) || worldMap.length === 0) return { settings, added: [] };
  const availability = {
    ...deriveSideQuestAvailability(worldMap, townMapsCache),
    theme: settings.theme,
    darkness: settings.darknessLevel,
  };
  let seed = ((parseInt(settings.worldSeed, 10) || 1) + poolSize * 104729) % 233280 || 1;
  const rng = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const added = backfillSideQuests(settings.sideQuests, {
    availability,
    level: effectivePartyLevel(party),
    rng,
    pool,
  });
  const next = { ...settings, sideQuestPoolSize: poolSize };
  // Place unaccepted quests (old ones included) in home towns, so offers spread out.
  const existing = settings.sideQuests || [];
  const unplaced = existing.filter((q) => q?.status === 'available' && !q.giver?.town);
  const placedById = new Map(assignHomeTowns(unplaced, townMapsCache, rng, existing).map((q) => [q.id, q]));
  const placedExisting = existing.map((q) => placedById.get(q?.id) || q);
  const placedAdded = assignHomeTowns(added, townMapsCache, rng, placedExisting);
  if (placedAdded.length > 0 || placedExisting.some((q, i) => q !== existing[i])) {
    next.sideQuests = [...placedExisting, ...placedAdded];
  }
  return { settings: next, added: placedAdded };
};

/**
 * Effective party level for quest gating: the lead hero's level plus a bonus for party
 * size (a full party can take on quests above the lead's level — your call). Roughly
 * +1 per two extra members.
 */
export const effectivePartyLevel = (party) => {
  const heroes = (party || []).filter(Boolean);
  if (heroes.length === 0) return 1;
  const lead = Math.max(...heroes.map((h) => h.level || 1));
  return lead + Math.floor(heroes.length / 2);
};

/** Move a side quest from 'available' to 'active' (accept it). Returns a new array. */
export const acceptSideQuest = (sideQuests, questId) =>
  (sideQuests || []).map((q) => (q.id === questId && q.status === 'available' ? { ...q, status: 'active' } : q));

// --- Shared offer picker (rumour + quest board) ------------------------------
// Both the exploration rumour (maybeOfferRumour) and the town Quest Board route
// through this ONE function so they share the same anti-flood guardrails and can
// never both flood the player. It cannot invent quests: it only ever surfaces a
// quest already present in `sideQuests` with status 'available' (a finite pool,
// topped up by backfillSideQuests), which is the primary over-spawn cap.

/** Max side quests the player may have ACTIVE at once before offers dry up. */
export const ACTIVE_QUEST_CAP = 3;
/** Moves that must pass between any two quest offers (board or rumour). */
export const OFFER_COOLDOWN_MOVES = 4;

/**
 * Pick one offerable side quest, or null when nothing should be offered. Pure and
 * deterministic (no Math.random) so both callers and unit tests share the guardrails.
 * Applied in order:
 *   1. status 'available' only, with minLevel <= effectivePartyLevel(party);
 *   2. active-quest cap: null once ACTIVE side quests >= activeCap;
 *   3. cooldown: null while (now - lastOfferAt) is within cooldownMoves;
 *   4. deterministic choice from the remaining pool (index derived from `now`, so
 *      successive offers rotate rather than always yielding the same quest).
 * @param {Array} sideQuests - the settings.sideQuests pool.
 * @param {Array} party - hero party (for effectivePartyLevel).
 * @param {Object} [opts]
 * @param {number} [opts.now=0] - a monotonic move counter (the clock).
 * @param {number|null} [opts.lastOfferAt=null] - clock value when an offer was last shown.
 * @param {number} [opts.cooldownMoves=OFFER_COOLDOWN_MOVES]
 * @param {number} [opts.activeCap=ACTIVE_QUEST_CAP]
 * @returns {Object|null} the chosen quest, or null when nothing is offerable.
 */
export const pickOfferableSideQuest = (sideQuests, party, opts = {}) => {
  const {
    now = 0,
    lastOfferAt = null,
    cooldownMoves = OFFER_COOLDOWN_MOVES,
    activeCap = ACTIVE_QUEST_CAP,
  } = opts;

  // 2. Active-quest cap: don't pile more objectives on an already-busy party.
  if (getActiveSideQuests(sideQuests).length >= activeCap) return null;

  // 3. Cooldown: a valid (non-negative, in-window) gap since the last offer blocks.
  //    A missing lastOfferAt, or a clock that ran backwards (e.g. a stale reload),
  //    counts as "no recent offer" so old saves and reseeded clocks still offer.
  if (lastOfferAt != null && Number.isFinite(now)) {
    const elapsed = now - lastOfferAt;
    if (elapsed >= 0 && elapsed < cooldownMoves) return null;
  }

  // 1. Eligible pool: available, in-level.
  const level = effectivePartyLevel(party);
  const eligible = getAvailableSideQuests(sideQuests).filter((q) => (q.minLevel || 1) <= level
    // one quest per building at a time: skip a quest whose home building is still busy
    && !(q.giver?.town && isGiverBusy(sideQuests, q.giver.town, [].concat(q.giver.building))));
  if (eligible.length === 0) return null;

  // 4. Deterministic rotate: index from the stable clock, never Math.random.
  const idx = ((Math.floor(now) % eligible.length) + eligible.length) % eligible.length;
  return eligible[idx];
};

/**
 * Run a game event against every ACTIVE side quest, completing matching event-steps
 * (item / enemy / location, with count support). Turn-in steps are NOT event-driven (see
 * turnInQuest). A quest completes only when ALL its steps (including any turn-in) are done.
 * @returns {{ updatedSideQuests, completions }} completions = one per step completed now.
 */
export const checkSideQuestEvent = (sideQuests, event) => {
  const completions = [];
  const updatedSideQuests = (sideQuests || []).map((q) => {
    if (q.status !== 'active') return q;
    const milestones = q.milestones.map((m) => ({ ...m }));
    let changed = false;
    for (const step of milestones) {
      if (step.completed || step.trigger?.turnIn) continue;     // skip done + turn-in steps
      if (!requiresMet(step, milestones)) continue;
      const res = applyEventToStep(step, event);
      if (!res) continue;
      step.progress = res.progress;
      changed = true;
      if (res.completed) {
        step.completed = true;
        completions.push({ questId: q.id, title: q.title, milestone: step, rewards: stepRewards(step), questCompleted: false });
      }
      break; // one step advances per event
    }
    if (!changed) return q;
    return finishIfDone({ ...q, milestones }, completions);
  });
  return { updatedSideQuests, completions };
};

/**
 * Hand in a quest at a building/town (the return-to-giver / courier turn-in). Completes any
 * READY turn-in step (prereqs met) whose target matches the context.
 * @param {Array} sideQuests
 * @param {{ buildingType?: string, townName?: string }} ctx - where the player is handing in.
 * @returns {{ updatedSideQuests, completions }}
 */
export const turnInQuest = (sideQuests, ctx = {}) => {
  const completions = [];
  const updatedSideQuests = (sideQuests || []).map((q) => {
    if (q.status !== 'active') return q;
    const milestones = q.milestones.map((m) => ({ ...m }));
    let changed = false;
    for (const step of milestones) {
      if (step.completed || !step.trigger?.turnIn) continue;
      if (!requiresMet(step, milestones)) continue;
      if (!turnInMatches(step.trigger.turnIn, ctx)) continue;
      step.completed = true;
      changed = true;
      completions.push({ questId: q.id, title: q.title, milestone: step, rewards: stepRewards(step), questCompleted: false });
      break;
    }
    if (!changed) return q;
    return finishIfDone({ ...q, milestones }, completions);
  });
  return { updatedSideQuests, completions };
};

// Mark a quest completed if every step is done, attaching the quest reward to the last
// completion entry for that quest.
const finishIfDone = (quest, completions) => {
  if (!quest.milestones.every((m) => m.completed)) return quest;
  const last = completions.filter((c) => c.questId === quest.id).pop();
  if (last) { last.questCompleted = true; last.questRewards = quest.rewards || null; }
  return { ...quest, status: 'completed' };
};

/** Available quests OFFERED at this building/town (their giver matches the context). */
export const getAvailableQuestsAt = (sideQuests, ctx = {}) =>
  getAvailableSideQuests(sideQuests).filter((q) => {
    const g = q.giver || {};
    if (!g.building) return false;
    const buildings = Array.isArray(g.building) ? g.building : [g.building];
    if (!buildings.includes(ctx.buildingType)) return false;
    if (g.location && ctx.townName !== g.location) return false;
    if (g.town && ctx.townName && ctx.townName !== g.town) return false;
    if (ctx.level != null && (q.minLevel || 1) > ctx.level) return false; // tiered reveal
    return true;
  });

/**
 * The side quest a building offers right now: at most one, and none while a quest taken
 * from that same building (same town and building type) is still active. Paces offers so
 * a busy town doesn't hand out a pile of quests at once.
 */
export const getOfferAt = (sideQuests, ctx = {}) => {
  if (isGiverBusy(sideQuests, ctx.townName, [ctx.buildingType])) return [];
  const here = getAvailableQuestsAt(sideQuests, ctx);
  if (!ctx.townName) return here.slice(0, 1);
  // Per-town pacing: only TOWN_OFFER_CAP of a town's quests are in play at once
  // (active + offered), lowest level first; finishing one opens the next.
  const open = openTownOffers(sideQuests, ctx.townName, ctx.level);
  return here.filter((q) => !q.giver?.town || open.has(q.id)).slice(0, 1);
};

/** Ids of a town's available quests currently open for offer (see TOWN_OFFER_CAP). */
export const openTownOffers = (sideQuests, townName, level = null) => {
  const homed = (q) => q.giver?.town === townName;
  const activeHere = getActiveSideQuests(sideQuests).filter(homed).length;
  const slots = Math.max(0, TOWN_OFFER_CAP - activeHere);
  return new Set(getAvailableSideQuests(sideQuests)
    .filter((q) => homed(q) && (level == null || (q.minLevel || 1) <= level))
    .sort((a, b) => ((a.minLevel || 1) - (b.minLevel || 1)) || String(a.id).localeCompare(String(b.id)))
    .slice(0, slots)
    .map((q) => q.id));
};

// Is a quest taken from this building type (in this town, when known) still active?
const isGiverBusy = (sideQuests, town, buildingTypes) => getActiveSideQuests(sideQuests).some((q) => {
  const g = q.giver || {};
  const buildings = Array.isArray(g.building) ? g.building : [g.building];
  return buildings.some((b) => buildingTypes.includes(b)) && (!town || !g.town || g.town === town);
});

/** Quests with a turn-in step ready to hand in at this context (for building UI). */
export const getReadyTurnIns = (sideQuests, ctx = {}) =>
  getActiveSideQuests(sideQuests).filter((q) => getReadyTurnInStep(q, ctx) !== null);

/** The turn-in step of `quest` that is ready to hand in at this context, or null. */
export const getReadyTurnInStep = (quest, ctx = {}) =>
  (quest?.milestones || []).find((s) => s.trigger?.turnIn && !s.completed && requiresMet(s, quest.milestones) && turnInMatches(s.trigger.turnIn, ctx)) || null;

export const getActiveSideQuests = (sideQuests) => (sideQuests || []).filter((q) => q.status === 'active');
export const getAvailableSideQuests = (sideQuests) => (sideQuests || []).filter((q) => q.status === 'available');
export const getCompletedSideQuests = (sideQuests) => (sideQuests || []).filter((q) => q.status === 'completed');

/** Step progress for a quest, e.g. for the quest log. */
export const getSideQuestProgress = (quest) => {
  const total = quest?.milestones?.length || 0;
  const done = (quest?.milestones || []).filter((m) => m.completed).length;
  return { done, total };
};

/**
 * Site types revealed on the world map: a cave/ruins becomes visible (and stays — sticky)
 * once a quest targeting it has been picked up (active) or finished (completed). Quests
 * still merely 'available' don't reveal anything — sites stay secret until you take the quest.
 * @returns {{ cave?: boolean, ruins?: boolean }}
 */
export const getRevealedSiteTypes = (sideQuests) => {
  const revealed = {};
  (sideQuests || []).forEach((q) => {
    if (q.status === 'available') return;
    q.milestones.forEach((m) => {
      if (m.site) revealed[m.site.type] = true;
      // Gather steps reveal their source sites too, so e.g. taking "collect 3 cave
      // mushrooms" uncovers the cave the mushrooms grow in.
      (m.sites || []).forEach((t) => { revealed[t] = true; });
    });
  });
  return revealed;
};

/**
 * Collect the site objectives quest chains want, keyed by site type ('cave'|'ruins'),
 * for ACTIVE quests only — so a site reveals/injects only once its quest is picked up.
 * Mirrors requiredBuildings for towns. Returns an ARRAY per type: several active quests
 * can target the same site type and each objective gets its own content slot (playtest
 * 2026-07-04: first-quest-wins left the second quest's item uncollectable).
 * @returns {{ cave?: Array, ruins?: Array }}
 */
export const getActiveSiteObjectives = (sideQuests) => {
  const byType = {};
  getActiveSideQuests(sideQuests).forEach((q) => {
    q.milestones.forEach((m) => {
      // A later step's objective is only placed once its prerequisites are done: a pickup
      // or kill before then would not count, and the objective would not come back.
      if (m.site && !m.completed && requiresMet(m, q.milestones)) {
        (byType[m.site.type] = byType[m.site.type] || []).push({ ...m.site, milestoneId: m.id, questId: q.id });
      }
    });
  });
  return byType;
};

/**
 * What each site type must SUPPLY for ACTIVE gather steps, keyed by site type. Parallel to
 * getActiveSiteObjectives, but for gather steps, which carry a `sites` SOURCE HINT (not a
 * `site` object) so getActiveSiteObjectives (and thus the injectSiteObjective path) excludes
 * them. Their item is sourced purely from a site's original harvest nodes, which are consumed
 * permanently on pickup and never regenerate, so a player who looted the site before taking
 * the quest is stranded. The site-entry code feeds this to injectHarvestResource to re-supply
 * the shortfall on the already-cached grid.
 *
 * For each (siteType, itemId), needed = MAX over the matching steps of (count - progress),
 * floored at 0. MAX not SUM: a single pickup credits every matching step (checkSideQuestEvent
 * advances one step per event, but successive pickups advance the rest), so summing would
 * over-inject. Every site type listed in a step's `sites` gets that item.
 * @returns {{ [siteType: string]: Array<{ itemId: string, needed: number }> }}
 */
export const getActiveGatherResources = (sideQuests) => {
  // siteType -> itemId -> needed (max shortfall across matching active steps)
  const byType = {};
  getActiveSideQuests(sideQuests).forEach((q) => {
    q.milestones.forEach((m) => {
      if (m.completed || !requiresMet(m, q.milestones)) return;
      if (!Array.isArray(m.sites) || m.sites.length === 0) return;
      const itemId = m.trigger?.item;
      const count = m.trigger?.count;
      if (!itemId || !count) return;
      const needed = Math.max(0, count - (m.progress || 0));
      if (needed <= 0) return;
      m.sites.forEach((siteType) => {
        const items = (byType[siteType] = byType[siteType] || {});
        items[itemId] = Math.max(items[itemId] || 0, needed);
      });
    });
  });
  // Normalize to an array of { itemId, needed } per type (array shape mirrors
  // getActiveSiteObjectives so the site-entry code consumes both the same way).
  const out = {};
  Object.keys(byType).forEach((siteType) => {
    out[siteType] = Object.keys(byType[siteType]).map((itemId) => ({ itemId, needed: byType[siteType][itemId] }));
  });
  return out;
};
